import {
  FormFieldResponseDTO,
  FormFieldTypeDTO,
  FormFieldLookupTypeDTO,
} from '@/api/base/DTOs';
import { IFormBase, SingleFormField } from '@/forms/typings';

export type AdaptSingleFormFieldFn = (
  formFieldResponse: FormFieldResponseDTO,
  parentFieldId?: IFormBase['fieldId'],
) => SingleFormField;

export type SingleFormFieldPrimitiveAdapters = {
  toInput: AdaptSingleFormFieldFn;
  toRadioGroup: AdaptSingleFormFieldFn;
  toSelect: AdaptSingleFormFieldFn;
};

/**
 * Routes {@link FormFieldResponseDTO} to concrete single-field adapters.
 * Primitive (text / integer / decimal + lookup) rules stay here; other DTO
 * types are registered in {@link single-form-field-adapter-registry.init}.
 */
export class SingleFormFieldAdapterRegistry {
  private readonly simpleAdapters = new Map<
    FormFieldTypeDTO,
    AdaptSingleFormFieldFn
  >();

  private primitiveAdapters: SingleFormFieldPrimitiveAdapters | null = null;

  setPrimitiveAdapters(adapters: SingleFormFieldPrimitiveAdapters): this {
    this.primitiveAdapters = adapters;
    return this;
  }

  register(dtoType: FormFieldTypeDTO, adapt: AdaptSingleFormFieldFn): this {
    this.simpleAdapters.set(dtoType, adapt);
    return this;
  }

  adapt(
    formFieldResponse: FormFieldResponseDTO,
    parentFieldId?: IFormBase['fieldId'],
  ): SingleFormField {
    if (!this.primitiveAdapters) {
      throw new Error(
        'SingleFormFieldAdapterRegistry: primitive adapters not initialized',
      );
    }

    const { fieldType, lookupType, internalLookup } = formFieldResponse;
    const { toInput, toRadioGroup, toSelect } = this.primitiveAdapters;

    if (
      fieldType === FormFieldTypeDTO.Text ||
      fieldType === FormFieldTypeDTO.Integer ||
      fieldType === FormFieldTypeDTO.Decimal
    ) {
      if (
        lookupType === undefined ||
        lookupType === FormFieldLookupTypeDTO.None
      ) {
        return toInput(formFieldResponse, parentFieldId);
      }

      if (
        lookupType === FormFieldLookupTypeDTO.Internal &&
        internalLookup?.isRadioButton
      ) {
        return toRadioGroup(formFieldResponse, parentFieldId);
      }

      return toSelect(formFieldResponse, parentFieldId);
    }

    const registered = this.simpleAdapters.get(fieldType);
    if (registered) {
      return registered(formFieldResponse, parentFieldId);
    }

    if (fieldType === FormFieldTypeDTO.Unknown) {
      console.warn('Unknown Form Field:', formFieldResponse);
      return toInput(formFieldResponse, parentFieldId);
    }

    console.error('Unimplemented Form Field:', formFieldResponse);
    return toInput(formFieldResponse, parentFieldId);
  }
}

export const singleFormFieldAdapterRegistry = new SingleFormFieldAdapterRegistry();

export const adaptFormFieldResponseToSingleFormField: AdaptSingleFormFieldFn = (
  dto,
  parentFieldId,
) => singleFormFieldAdapterRegistry.adapt(dto, parentFieldId);
